import { useEffect, useState, useRef } from 'react';
import { ComboBox, Item, Key, Text } from '@adobe/react-spectrum';
import { DataComponentProps } from './DataComponentProps';
import { useTranslation } from 'react-i18next';
import { useDependency } from '../../../contexts/DependencyProvider';
import { ListItemRequest } from '../../../services/soap/item/requests/ListItemRequest';

// Extended interface to handle FormControl usage
export interface IItemDropDownProps extends DataComponentProps {
  itemType?: string;
  status?: string;
}

// Interface for FormControl usage
interface ItemDropDownFormControlProps {
  component: any;
  updateData: (key: string, value: any) => void;
  getData: (key: string, default_value: any) => any;
}

const ItemDropDown: React.FC<IItemDropDownProps> = ({ 
  compKey, 
  label, 
  width, 
  isRequired, 
  onValueChange, 
  value: dvalue, 
  onBlur,
  itemType,
  status 
}) => {
  const { t } = useTranslation();
  const { itemService, store } = useDependency();
  const _t = (key: string) => t(`newContextDialog.dataComponents.${key}`, { ns: 'finder' });
  const message = _t('is_required').split('[content]').join(label);
  const [errorMessage, setErrorMessage] = useState(isRequired ? message : '');
  const [value, setValue] = useState<Key | null>(null);
  const [input, setInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Load items data
  useEffect(() => {
    const loadItems = async () => {
      if (!isMounted.current) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const requestOptions: any = {
          offset: 0,
          limit: 200
        };
        
        if (searchText.trim()) {
          requestOptions.searchName = searchText.trim();
        }

        const response = await itemService.listItem(
          new ListItemRequest(
            store.Server,
            store.SessionId,
            requestOptions.limit,
            requestOptions.offset,
            requestOptions
          )
        );

        if (!isMounted.current) return;

        let itemList: any[] = [];
        if (response.result === 'OK' && response?.count > 0) {
          if (Array.isArray(response.ITEM)) {
            itemList = response.ITEM;
          } else if (response.ITEM) {
            itemList = [response.ITEM];
          }
        }

                 setItems(itemList);
      } catch (err) {
        if (isMounted.current) {
          setError('Failed to fetch items');
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };

    loadItems();
  }, [itemService, store.Server, store.SessionId, searchText]);

  useEffect(() => {
    // Ensure value is never undefined to prevent controlled/uncontrolled warning
    setValue(dvalue ? (dvalue as Key) : null);
    if(dvalue === '-1'){
      setErrorMessage(message);
    }
  }, [dvalue, message]);
  
  const onSelectionChange = (value: Key | null) => {
    if(onValueChange)
        onValueChange(value ? value.toString() : '', input);
    setValue(value ?? null);
    if(isRequired && !value) 
      setErrorMessage(message);
    else 
      setErrorMessage('');  
  };

  const onInputChange = (value: string) => {
    setInput(value ?? '');
    // setSearchText(value);
    if(isRequired && !value) 
      setErrorMessage(message);
    else 
      setErrorMessage('');
  }

  return (
    <ComboBox 
      key={compKey} 
      label={label} 
      width={width} 
      isRequired={isRequired} 
      selectedKey={value ?? null}
      onSelectionChange={onSelectionChange} 
      onInputChange={onInputChange}
      validationState={errorMessage ? 'invalid' : undefined}
      errorMessage={errorMessage}
      onBlur={onBlur}
      >
             {loading ? (
         <Item key="loading" textValue="Loading...">Loading...</Item>
       ) : (
         items && items.map((item: any) => {
           const displayText = `${item.name} (${item.refId})`;
           return (
             <Item key={item.id} textValue={displayText}>
               <Text>{displayText}</Text>
             </Item>
           );
         })
       )}
    </ComboBox>
  );
};

// Wrapper component for FormControl usage
export const ItemDropDownFormControl: React.FC<ItemDropDownFormControlProps> = ({ 
  component, 
  updateData, 
  getData 
}) => {
  const handleValueChange = (value: any) => {
    updateData(component.FORMITEM, value);
  }

  return (
    <ItemDropDown
      compKey={component.FORMITEM}
      label={component.PROPERTIES.name || component.PROPERTIES.caption || 'Item'}
      width={'100%'}
      value={getData(component.FORMITEM, '')}
      isRequired={component.PROPERTIES.required === 'true'}
      onValueChange={handleValueChange}
      itemType={(component.PROPERTIES as any).itemType}
      status={(component.PROPERTIES as any).status}
    />
  );
};

export default ItemDropDown; 